import type { ShiftProductionFormPayload } from "../../../shared/shift-production-form/contracts";
import { emptyProduct } from "./form-payload";

export type ProductSection = ShiftProductionFormPayload["products"][number];
export type Measurement = ProductSection["measurements"][number];
export type SpeedChange = ProductSection["speedChanges"][number];
export type Downtime = ProductSection["downtimes"][number];
export type ProductChange = ProductSection["productChanges"][number];

export function updateProduct(
  payload: ShiftProductionFormPayload,
  productIndex: number,
  update: (product: ProductSection) => ProductSection,
) {
  return {
    ...payload,
    products: payload.products.map((product, index) =>
      index === productIndex ? update(product) : product,
    ),
  };
}

export function updateMeasurement(
  payload: ShiftProductionFormPayload,
  productIndex: number,
  key: Measurement["key"],
  update: (measurement: Measurement) => Measurement,
) {
  return updateProduct(payload, productIndex, (product) => ({
    ...product,
    measurements: product.measurements.map((measurement) =>
      measurement.key === key ? update(measurement) : measurement,
    ),
  }));
}

export function addProduct(payload: ShiftProductionFormPayload) {
  return { ...payload, products: [...payload.products, emptyProduct()] };
}

export function removeProduct(
  payload: ShiftProductionFormPayload,
  productIndex: number,
) {
  return {
    ...payload,
    products: payload.products.filter(
      (_product, index) => index !== productIndex,
    ),
  };
}

export function addProductEvent<
  K extends "speedChanges" | "downtimes" | "productChanges",
>(
  payload: ShiftProductionFormPayload,
  productIndex: number,
  key: K,
  value: ProductSection[K][number],
) {
  return updateProduct(payload, productIndex, (product) => ({
    ...product,
    [key]: [...product[key], value],
  }));
}

export function updateProductEvent<
  K extends "speedChanges" | "downtimes" | "productChanges",
>(
  payload: ShiftProductionFormPayload,
  productIndex: number,
  key: K,
  eventIndex: number,
  update: (value: ProductSection[K][number]) => ProductSection[K][number],
) {
  return updateProduct(payload, productIndex, (product) => ({
    ...product,
    [key]: product[key].map((value, index) =>
      index === eventIndex ? update(value) : value,
    ),
  }));
}

export function removeProductEvent<
  K extends "speedChanges" | "downtimes" | "productChanges",
>(
  payload: ShiftProductionFormPayload,
  productIndex: number,
  key: K,
  eventIndex: number,
) {
  return updateProduct(payload, productIndex, (product) => ({
    ...product,
    [key]: product[key].filter((_value, index) => index !== eventIndex),
  }));
}

export function numberOrNull(value: string) {
  if (value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function textOrNull(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function localDateTimeToInstant(value: string) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function instantToLocalDateTime(value: string | null) {
  if (!value) return "";
  const date = new Date(value);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export type ShiftFormValidationIssue = {
  field: string;
  message: string;
};

const requiredMeasurementKeys = new Set<Measurement["key"]>([
  "ACTUAL_LINE_SPEED",
  "UNIT_WEIGHT",
  "PRODUCT_MOISTURE",
  "AMBIENT_TEMPERATURE",
  "AMBIENT_HUMIDITY",
]);

export function validationMessage(
  issues: readonly ShiftFormValidationIssue[],
  field: string,
) {
  return issues.find((issue) => issue.field === field)?.message ?? null;
}

export function validatePayloadForSubmitIssues(payload: ShiftProductionFormPayload) {
  const issues: ShiftFormValidationIssue[] = [];
  const add = (field: string, message: string) => issues.push({ field, message });
  if (!payload.operatorName.trim()) add("operatorName", "Operatör adı zorunludur.");
  if (!payload.departmentName) add("departmentName", "Bölüm seçilmelidir.");
  if (!payload.lineName) add("lineName", "Hat seçilmelidir.");
  if (!payload.productionDate) add("productionDate", "Üretim tarihi zorunludur.");
  if (!payload.shift) add("shift", "Vardiya seçilmelidir.");
  for (const item of payload.checklist) {
    if (item.state !== "SUITABLE" && item.state !== "UNSUITABLE")
      add(`checklist.${item.key}.state`, "Uygun veya Değil seçilmelidir.");
  }
  const unsuitableItems = payload.checklist.filter(
    (item) => item.state === "UNSUITABLE",
  );
  if (unsuitableItems.some((item) => !item.explanation.trim()))
    for (const item of unsuitableItems.filter((item) => !item.explanation.trim()))
      add(`checklist.${item.key}.explanation`, "Uygunsuzluk açıklaması zorunludur.");
  if (payload.products.length === 0)
    add("products", "En az bir ürün bölümü bulunmalıdır.");
  payload.products.forEach((product, index) => {
    const label = `Ürün ${index + 1}`;
    const prefix = `products.${index}`;
    if (product.productionOccurred) {
      if (!product.machineStartedAt && !product.machineStartNotOccurredReason?.trim())
        add(`${prefix}.machineStart`, `${label}: makina start zamanı veya gerçekleşmeme sebebi zorunludur.`);
      if (!product.firstProductAt && !product.firstProductNotOccurredReason?.trim())
        add(`${prefix}.firstProduct`, `${label}: makinadan ürün çıkma zamanı veya gerçekleşmeme sebebi zorunludur.`);
    } else if (!product.noProductionReason?.trim()) {
      add(`${prefix}.noProductionReason`, `${label}: üretim yapılmama nedeni zorunludur.`);
    }
    for (const measurement of product.measurements) {
      const required = requiredMeasurementKeys.has(measurement.key);
      if (required && !measurement.state)
        add(`${prefix}.measurements.${measurement.key}`, `${label}: bu parametre için değer girilmelidir.`);
      if (measurement.state &&
        measurement.state === "MEASURED" &&
        (measurement.value === null || !measurement.unit)
      )
        add(`${prefix}.measurements.${measurement.key}`, `${label}: değer ve birim birlikte girilmelidir.`);
      if (measurement.state === "NOT_MEASURED" && !measurement.missingReason)
        add(`${prefix}.measurements.${measurement.key}`, `${label}: ölçülmeyen alanın nedeni zorunludur.`);
      if (measurement.missingReason === "OTHER" && !measurement.explanation?.trim())
        add(`${prefix}.measurements.${measurement.key}`, `${label}: diğer ölçülmeme nedeni açıklanmalıdır.`);
      if (measurement.state !== "MEASURED" && (measurement.value !== null || measurement.measuredAt !== null))
        add(`${prefix}.measurements.${measurement.key}`, `${label}: ölçülmeyen veya uygulanmaz alanda değer olamaz.`);
      if (
        measurement.value !== null &&
        !["MACHINE_TEMPERATURE", "OVEN_TEMPERATURE", "MOLD_TEMPERATURE", "TUNNEL_TEMPERATURE", "AMBIENT_TEMPERATURE"].includes(measurement.key) &&
        measurement.value < 0
      )
        add(`${prefix}.measurements.${measurement.key}`, `${label}: değer sıfırdan küçük olamaz.`);
    }
    for (const change of product.speedChanges) {
      if (change.newSpeed === null || !change.unit)
        add(`${prefix}.speedChanges`, `${label}: hız değişiminde yeni hız ve birim zorunludur.`);
      if (change.newSpeed !== null && change.newSpeed < 0)
        add(`${prefix}.speedChanges`, `${label}: hız sıfırdan küçük olamaz.`);
      if (!change.reason.trim())
        add(`${prefix}.speedChanges`, `${label}: hız değişimi nedeni zorunludur.`);
      if (!change.notInformed && !change.informedPerson?.trim())
        add(`${prefix}.speedChanges`, `${label}: bilgi verilen yetkili zorunludur.`);
    }
    for (const downtime of product.downtimes) {
      if (!downtime.startedAt || (!downtime.endedAt && !downtime.carriedToNextShift))
        add(`${prefix}.downtimes`, `${label}: duruş başlangıç ve bitişi zorunludur.`);
      if (!downtime.reason.trim() || !downtime.responsibleDepartment.trim())
        add(`${prefix}.downtimes`, `${label}: duruş nedeni ve sorumlu bölüm zorunludur.`);
    }
    for (const change of product.productChanges) {
      if (!change.reason.trim() || change.participantCount === null || !change.responsibleDepartment.trim())
        add(`${prefix}.productChanges`, `${label}: ürün değişiminde sebep, adam sayısı ve sorumlu bölüm zorunludur.`);
      if (change.participantCount !== null && change.participantCount < 0)
        add(`${prefix}.productChanges`, `${label}: adam sayısı sıfırdan küçük olamaz.`);
      if (change.participantCount !== null && !Number.isInteger(change.participantCount))
        add(`${prefix}.productChanges`, `${label}: adam sayısı tam sayı olmalıdır.`);
    }
    if (product.productChanges.length > 0) {
      if (!product.postChangeMachineStartedAt && !product.postChangeDelayReason?.trim())
        add(`${prefix}.postChangeMachineStart`, `${label}: ürün değişiminden sonra makina başlangıcı veya gecikme sebebi zorunludur.`);
      if (!product.postChangeFirstProductAt && !product.postChangeFirstProductDelayReason?.trim())
        add(`${prefix}.postChangeFirstProduct`, `${label}: ürün değişiminden sonra ilk ürün çıkışı veya gecikme sebebi zorunludur.`);
    }
    if (product.machineStartedAt && product.firstProductAt && Date.parse(product.firstProductAt) < Date.parse(product.machineStartedAt))
      add(`${prefix}.firstProduct`, `${label}: ilk ürün çıkışı makina başlangıcından önce olamaz.`);
    if (product.postChangeMachineStartedAt && product.postChangeFirstProductAt && Date.parse(product.postChangeFirstProductAt) < Date.parse(product.postChangeMachineStartedAt))
      add(`${prefix}.postChangeFirstProduct`, `${label}: ürün değişimi sonrası ilk ürün çıkışı makina başlangıcından önce olamaz.`);
    const hasWaste = [
      product.quantities.scrapKg,
      product.quantities.animalFeedKg,
      product.quantities.reelWasteQuantity,
      product.quantities.boxWasteCount,
      product.quantities.cartonWasteCount,
      product.quantities.labelWasteCount,
    ].some((value) => value !== null && value > 0);
    if (
      [
        product.quantities.scrapKg,
        product.quantities.animalFeedKg,
        product.quantities.reelWasteQuantity,
        product.quantities.boxWasteCount,
        product.quantities.cartonWasteCount,
        product.quantities.labelWasteCount,
      ].some((value) => value !== null && value < 0)
    )
      add(`${prefix}.wasteReason`, `${label}: miktar sıfırdan küçük olamaz.`);
    if (
      [
        product.quantities.targetCarton,
        product.quantities.targetCount,
        product.quantities.targetKg,
        product.quantities.actualCarton,
        product.quantities.actualCount,
        product.quantities.actualKg,
      ].some((value) => value !== null && value < 0)
    )
      add(`${prefix}.targetQuantity`, `${label}: miktar sıfırdan küçük olamaz.`);
    if (
      [
        product.quantities.boxWasteCount,
        product.quantities.cartonWasteCount,
        product.quantities.labelWasteCount,
        product.quantities.targetCarton,
        product.quantities.targetCount,
        product.quantities.actualCarton,
        product.quantities.actualCount,
      ].some((value) => value !== null && !Number.isInteger(value))
    )
      add(`${prefix}.targetQuantity`, `${label}: adet miktarı tam sayı olmalıdır.`);
    if (
      hasWaste &&
      !product.quantities.wasteReason?.trim()
    )
      add(`${prefix}.wasteReason`, `${label}: fire nedeni zorunludur.`);
    if (![
      product.quantities.actualCarton,
      product.quantities.actualCount,
      product.quantities.actualKg,
    ].some((value) => value !== null))
      add(`${prefix}.actualQuantity`, `${label}: en az bir gerçekleşen üretim miktarı zorunludur.`);
  });
  return issues.filter(
    (issue, index, all) =>
      all.findIndex((entry) => entry.field === issue.field && entry.message === issue.message) === index,
  );
}

export function validatePayloadForSubmit(payload: ShiftProductionFormPayload) {
  return [...new Set(validatePayloadForSubmitIssues(payload).map((issue) => issue.message))];
}
